import { css, styled } from '../../../common.styles';
import { baseColor, mobile, normalFont, primaryFont, tablet } from '../../../styles/general/theme';
import React from 'react';
import { ButtonProps } from '../general/common-button';

const BaseStyle = css<{ fullWidth?: boolean }>`
  font-family: ${primaryFont};
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  text-transform: uppercase;
  border-radius: 26px;
  padding: 9px 23px;
  cursor: pointer;
  user-select: none;
  width: fit-content;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  text-decoration: none;
  min-width: max-content;

  ${(p) =>
    p.fullWidth &&
    css`
      width: 100%;
      justify-content: center;
    `};

  @media (max-width: ${tablet}px) {
    justify-content: center;
  }

  @media (max-width: ${mobile}px) {
    justify-content: center;
    width: 100%;
  }
`;

export const FiatButtonStyle = css<{ darkBackground?: boolean }>`
  ${BaseStyle};
  color: ${(p) => (p.darkBackground ? baseColor(100) : baseColor(900))};
  background-color: ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};
  pointer-events: all;

  :hover {
    background-color: ${(p) => (p.darkBackground ? baseColor(800) : baseColor(700))};
  }
`;

export const DisabledFiatStyle = css<{ disabled?: boolean | undefined; darkBackground?: boolean; fullWidth?: boolean }>`
  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      color: ${p.darkBackground ? baseColor(100) : baseColor(730)};
      background-color: ${p.darkBackground ? baseColor(700) : baseColor(750)};

      :hover {
        background-color: ${p.darkBackground ? baseColor(700) : baseColor(750)};
      }
    `};
`;

export const FiatSecondaryButtonStyle = css<{ darkBackground?: boolean }>`
  ${BaseStyle};
  color: ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};
  background-color: ${(p) => (p.darkBackground ? baseColor(100) : baseColor(900))};
  border: 1px solid ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};

  :hover {
    background-color: ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};
    border: 1px solid ${(p) => (p.darkBackground ? baseColor(900) : baseColor(700))};
    color: ${(p) => (p.darkBackground ? baseColor(100) : baseColor(900))};
  }
`;

export const DisabledSecondaryFiatStyle = css<{ disabled?: boolean | undefined; darkBackground?: boolean }>`
  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      border-color: ${p.darkBackground ? baseColor(730) : baseColor(730)};
      color: ${p.darkBackground ? baseColor(730) : baseColor(730)};
      background-color: ${p.darkBackground ? baseColor(100) : baseColor(900)};

      :hover {
        background-color: ${p.darkBackground ? baseColor(100) : baseColor(900)};
        border: 1px solid ${p.darkBackground ? baseColor(730) : baseColor(730)};
        color: ${p.darkBackground ? baseColor(730) : baseColor(730)};
      }
    `};
`;

export const FiatPrimaryButton = styled.a<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  darkMode?: boolean;
}>`
  ${FiatButtonStyle};
  ${DisabledFiatStyle};

  ${(p) =>
    p.darkMode &&
    css`
      background-color: ${baseColor(900)};
      border-color: ${baseColor(900)};
      color: ${baseColor(100)};

      :hover {
        background-color: ${baseColor(800)};
        border-color: ${baseColor(800)};
        color: ${baseColor(100)};
      }
    `};
`;

export const FiatSecondaryButton = styled.a<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  darkMode?: boolean;
}>`
  ${FiatSecondaryButtonStyle};
  ${DisabledSecondaryFiatStyle};

  ${(p) =>
    p.darkMode &&
    css`
      background-color: transparent;
      border-color: ${baseColor(900)};
      color: ${baseColor(900)};

      :hover {
        background-color: ${baseColor(900)};
        border-color: ${baseColor(900)};
        color: ${baseColor(100)};
      }
    `};
`;

export const FiatChevronLeftArrowIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path
      d="M19.3199 25.262L20.1409 24.441L11.7809 16.091L20.0409 7.821L19.2199 7L10.1389 16.091L19.3199 25.262Z"
      fill="black"
    />
  </svg>
);

export const FiatChevronRightArrowIcon: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_13552_22467)">
      <path d="M12.6999 7L11.8999 7.8L20.2999 16.2L11.9999 24.4L12.7999 25.2L21.8999 16.1L12.6999 7Z" fill="black" />
    </g>
    <defs>
      <clipPath id="clip0_13552_22467">
        <rect width="32" height="32" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const FiatArrowAndTextButtonWrapper = styled.a`
  font-family: ${normalFont};
  letter-spacing: 1px;
  cursor: pointer;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${baseColor(100)};
`;

const FiatTextButtonWrapper = styled.a<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  darkMode?: boolean;
}>`
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  color: ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};
  text-decoration-line: underline;
  padding: 9px 23px;
  line-height: 32px;
  box-sizing: border-box;

  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      color: ${baseColor(730)};
    `};

  &:hover {
    text-decoration: none;
  }

  ${(p) =>
    p.darkMode &&
    css`
      color: ${baseColor(900)};

      :hover {
        color: ${baseColor(900)};
      }
    `};
`;

export const FiatTextButton: React.FC<ButtonProps> = (props) => {
  return <FiatTextButtonWrapper {...props}>{props.children}</FiatTextButtonWrapper>;
};

export const FiatArrowAndTextButton: React.FC<ButtonProps> = (props) => {
  const position = props.iconPosition || 'left';
  return (
    <FiatArrowAndTextButtonWrapper {...props}>
      {position === 'left' && <FiatChevronLeftArrowIcon />}
      {props.children}
      {position === 'right' && <FiatChevronRightArrowIcon />}
    </FiatArrowAndTextButtonWrapper>
  );
};

export const FiatButton = (props: ButtonProps): any => {
  switch (props?.variant?.toLowerCase()) {
    case 'primary':
      return <FiatPrimaryButton {...props} />;
    case 'secondary':
      return <FiatSecondaryButton {...props} />;
    case 'tertiary':
      return <FiatTextButton {...props} />;
    case 'icon':
      return <FiatArrowAndTextButton {...props}>{props.children}</FiatArrowAndTextButton>;
    default:
      return null;
  }
};
